import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, PrimaryButton, GhostButton, Modal, Input, Select, Textarea, Table } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { FileText, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Enquiries() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [sports, setSports] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ institution_id: "", type: "event", title: "", sport: "", preferred_start: "", preferred_end: "", headcount: 20, destination: "", notes: "" });

  const load = () => api.get("/enquiries").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => {
    load();
    api.get("/institutions").then((r) => setInstitutions(r.data)).catch(() => {});
    api.get("/sports").then((r) => setSports(r.data)).catch(() => {});
  }, []);

  const verified = institutions.filter((i) => i.status === "verified");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openModal = () => {
    setForm((f) => ({ ...f, institution_id: verified[0]?.id || "" }));
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.institution_id) { toast.error("Select a verified institution first"); return; }
    setSaving(true);
    try {
      await api.post("/enquiries", { ...form, headcount: Number(form.headcount) });
      toast.success("Enquiry submitted"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  if (!list) return <Spinner />;

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Enquiries" subtitle="Event and sports-tourism requests"
        action={!hasRole("finance") && <PrimaryButton data-testid="add-enquiry-btn" onClick={openModal}><Plus className="h-4 w-4" /> New enquiry</PrimaryButton>} />

      {list.length === 0 ? (
        <EmptyState icon={FileText} title="No enquiries yet" text="Submit an event or sports-tourism enquiry for a verified institution."
          action={<PrimaryButton onClick={openModal}><Plus className="h-4 w-4" /> New enquiry</PrimaryButton>} />
      ) : (
        <Table head={["Reference", "Title", "Type", "Sport", "Institution", "Headcount", "Status"]}>
          {list.map((e) => (
            <tr key={e.id} data-testid={`enquiry-row-${e.id}`} onClick={() => navigate(`/app/enquiries/${e.id}`)}
              className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-background/40">
              <td className="px-4 py-3 font-mono text-xs text-primary">{e.reference}</td>
              <td className="px-4 py-3 font-medium">{e.title}</td>
              <td className="px-4 py-3 capitalize text-muted-foreground">{e.type}</td>
              <td className="px-4 py-3 text-muted-foreground">{e.sport}</td>
              <td className="px-4 py-3 text-muted-foreground">{e.institution_name}</td>
              <td className="px-4 py-3 font-mono text-muted-foreground">{e.headcount}</td>
              <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
            </tr>
          ))}
        </Table>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New enquiry" wide>
        {verified.length === 0 ? (
          <p className="text-sm text-muted-foreground">You need at least one <strong>verified</strong> institution before submitting an enquiry. Register and get an institution verified first.</p>
        ) : (
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
            <Select label="Institution" testId="enq-institution" value={form.institution_id} onChange={set("institution_id")} className="sm:col-span-2">
              {verified.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </Select>
            <Select label="Type" testId="enq-type" value={form.type} onChange={set("type")}>
              <option value="event">Sports Event</option><option value="tourism">Sports Tourism / Trip</option>
            </Select>
            <Select label="Sport" testId="enq-sport" value={form.sport} onChange={set("sport")}>
              <option value="">Select sport</option>{sports.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input label="Title" testId="enq-title" required value={form.title} onChange={set("title")} className="sm:col-span-2" placeholder="e.g. Inter-school athletics meet" />
            <Input label="Preferred start date" testId="enq-start" type="date" required value={form.preferred_start} onChange={set("preferred_start")} />
            <Input label="Preferred end date" testId="enq-end" type="date" value={form.preferred_end} onChange={set("preferred_end")} />
            <Input label="Headcount" testId="enq-headcount" type="number" min={1} required value={form.headcount} onChange={set("headcount")} />
            <Input label="Destination (for tourism)" testId="enq-destination" value={form.destination} onChange={set("destination")} />
            <Textarea label="Notes" testId="enq-notes" rows={2} value={form.notes} onChange={set("notes")} className="sm:col-span-2" />
            <div className="flex justify-end gap-2 sm:col-span-2">
              <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
              <PrimaryButton data-testid="save-enquiry-btn" type="submit" disabled={saving}>Submit enquiry</PrimaryButton>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
